/* eslint-disable no-param-reassign */
/**
 * Brand Safety slice.
 *
 * Live API: each endpoint thunk calls the real endpoint through the seam in `brandSafetyApi.js` and
 * returns the raw response; a failure surfaces as a real error (no mock fallback). Only
 * `fetchBrandSafetyReport` writes to Redux, so only it branches on success/error.
 *
 * Follows the Audiences slice conventions — plain setter reducers, hand-rolled thunks that return a
 * Promise (the API client is callback-based), no createAsyncThunk, no selectors. Components read
 * `state.BrandSafety` directly.
 */

import { createSlice } from '@reduxjs/toolkit';
import { isApiSuccess } from '../../constants/platformStatusCodes';
import {
  AVAILABILITY_TIMEOUT_MS,
  BRAND_SAFETY_ENDPOINTS as EP,
  BS_DETECTION_REVIEW_BATCH_SIZE,
  BS_FLAGGED_POSTS_LIMIT,
  BS_FLAG_OCCURRENCES_MAX_ITEMS,
  CAMPAIGN_REPORT_TIMEOUT_MS,
  apiGet,
  apiPatch,
  apiPost,
  buildApproveAllBody,
  buildAvailabilityBody,
  buildDetectionReviewBody,
  buildRunBody,
} from './brandSafetyApi';
import {
  aggregateTrademarkBrands,
  flagIdentityKey,
  normalizeBrandSafetyReport,
  normalizeFlagOccurrences,
  postDetections,
} from './utils/brandSafetyUtils';
import {
  aggregateCampaignBrandSafety,
  aggregateCampaignFlaggedPosts,
  // Re-derives the campaign-level hero status from the full per-creator union (used by `.checks.updated`).
  deriveCampaignStatus,
} from './campaign/campaignBrandSafetyModel';
// Source of truth shared with campaign creation: same flags, severity model and state shape.
import {
  getDefaultPreferences,
  normalizePreferences,
} from '../Persona/BrandSafety/brandSafetySeverity';
// Campaign preferences act as a ceiling on the per-influencer filters (see the helper's header).
import { clampPreferencesToCeiling, mergeUnderCeiling } from './utils/campaignFlagCeiling';

const initialState = {
  report: null,
  reportLoading: false,
  // True while a "Load more" page of flagged posts is in flight — separate from `reportLoading` so the
  // initial full-page loader and the incremental append spinner don't fight over the same flag.
  reportLoadingMore: false,
  reportError: null,
  // The crd whose report currently sits in `report` — the cache key. A revisit with the same crd reads
  // the store instead of re-fetching; a different crd (or a reset on a new scan) misses and re-fetches.
  reportFetchedCrd: null,
  /**
   * Per-creator report cache: `{ [crd]: { report, flagConfig } }`. Switching influencers swaps the active
   * `report` slot but keeps each creator's loaded pages here, so returning to a creator restores their
   * report (all pages appended so far) + their saved flag config with NO network call. Survives
   * `resetBrandSafetyReport` (it's a cache, not the active view); only a forced Refresh re-fetches.
   */
  reportsByCrd: {},
  /**
   * Flag-type group keys the user has resolved. Local-only until the resolve endpoint exists —
   * it lives in Redux rather than component state so it survives a tab switch within the report.
   */
  resolvedGroups: [],
  /**
   * Brand Safety Parameters — per-flag severity preferences from the Filters panel (Figma 853:3751).
   * Same shape campaign creation uses: { [Flag]: { severe, moderate, low, minor } }, all enabled by
   * default (see `getDefaultPreferences`). Held here (not component state) so it survives the drawer
   * closing and, later, drives the report server-side.
   */
  filters: getDefaultPreferences(),
  /**
   * The same map WITHOUT the campaign ceiling applied — the user's (or the persisted config's) actual
   * intent. `filters` is always `clamp(filtersBase, campaignFlagCeiling)`. Keeping the unclamped base
   * is what lets a widened ceiling restore a flag: re-clamping the already-clamped map would
   * intersect every ceiling the session ever saw, and nothing could ever turn back on.
   */
  filtersBase: getDefaultPreferences(),
  /**
   * True once the user edits the Filters drawer this session (before persisting on Done). Guards the
   * load-time hydration so a slow report load / retry can't overwrite an in-progress edit with the
   * server's (or the default) config. Reset when leaving the report.
   */
  filtersDirty: false,
  /**
   * The campaign's Brand Safety preferences, normalized — a CEILING on `filters`. A flag/severity the
   * campaign disabled is forced off no matter what the per-influencer config says, and the drawer
   * renders it as a locked toggle. `null` = no campaign context (deep-linked report) ⇒ no constraint.
   * Kept here so every reducer that writes `filters` can clamp — the drawer's own Reset bypasses the
   * severity panel, so a UI-only guard would be escapable.
   */
  campaignFlagCeiling: null,
  /**
   * Per-post review verdicts made in the Review tab (postId → 'approved' | 'rejected' | 'pending'),
   * overlaid on the report's `posts[]` so a resolved post updates the gallery/filters immediately.
   * Local until endpoint 4 is live (the resolve action already calls the ep-4 mock).
   */
  reviewedPosts: {},
  /**
   * Per-FLAGGED-ITEM review verdicts made in the Review tab's AI Findings rail
   * (`{ [postId]: { [detectionKey]: 'approved' | 'rejected' | 'pending' } }`), overlaid on each
   * detection card so an individually approved object/trademark/segment updates immediately.
   * Deliberately separate from `reviewedPosts`: a post only flips to approved once EVERY one of its
   * flagged items is resolved, so the two overlays move on different rules (see
   * `resolveReviewDetections`).
   */
  reviewedDetections: {},
  /**
   * Campaign-level Brand Safety (dashboard tab), built from creatorReports' `creatorReports[]` (doc #2)
   * via `aggregateCampaignBrandSafety`. Separate from the single-influencer `report` above.
   */
  campaign: {
    report: null,
    loading: false,
    error: null,
    /**
     * flaggedPosts (doc #4) aggregated to `{ summary, pagination, creators[] }` — grouped by creator,
     * paginated by creator ("Load more creators"). Drives the Media view + Review tab.
     */
    flagged: null,
    flaggedLoading: false,
    flaggedError: null,
    // True while a "Load more creators" page is in flight — separate from `flaggedLoading` so the initial
    // load spinner and the incremental append spinner don't fight over one flag.
    flaggedLoadingMore: false,
  },
};

const BrandSafetySlice = createSlice({
  name: 'BrandSafety',
  initialState,
  reducers: {
    setReport(state, action) {
      state.report = action.payload;
    },
    setReportLoading(state, action) {
      state.reportLoading = action.payload;
    },
    setReportLoadingMore(state, action) {
      state.reportLoadingMore = action.payload;
    },
    setReportError(state, action) {
      state.reportError = action.payload;
    },
    /**
     * Append a further page of flagged posts (Load more) to the loaded report WITHOUT disturbing the
     * lifetime aggregate widgets, which are identical across pages. De-dupes by post id, advances the
     * pagination cursor, and re-derives the trademark-by-brand table (its only source is the loaded
     * posts, since the BE has no lifetime per-brand aggregate) from the accumulated brands.
     */
    appendReportPosts(state, action) {
      if (!state.report) return;
      const { posts = [], brands = [], pagination = null } = action.payload || {};
      const existingPosts = Array.isArray(state.report.posts) ? state.report.posts : [];
      const seen = new Set(existingPosts.map((p) => String(p.id)));
      state.report.posts = existingPosts.concat(
        (Array.isArray(posts) ? posts : []).filter((p) => p && !seen.has(String(p.id)))
      );
      const existingBrands = Array.isArray(state.report.brands) ? state.report.brands : [];
      state.report.brands = existingBrands.concat(Array.isArray(brands) ? brands : []);
      state.report.trademarkBrands = aggregateTrademarkBrands(state.report.brands);
      if (pagination) state.report.pagination = pagination;
      // Mirror the freshly-appended report into the per-creator cache so a revisit restores every loaded page.
      const activeCrd = state.reportFetchedCrd;
      if (activeCrd != null && state.reportsByCrd[activeCrd]) {
        state.reportsByCrd[activeCrd].report = state.report;
      }
    },
    /** Records which crd's report is cached in `report` (the cache key). Set on a successful load. */
    setReportFetchedCrd(state, action) {
      state.reportFetchedCrd = action.payload == null ? null : String(action.payload);
    },
    /**
     * Snapshot a loaded report into the per-creator cache. Payload: { crd, report, flagConfig }. Keyed by
     * crd so switching influencers and back serves from here instead of re-fetching.
     */
    cacheReport(state, action) {
      const { crd, report, flagConfig = null } = action.payload || {};
      if (crd == null) return;
      state.reportsByCrd[String(crd)] = { report, flagConfig };
    },
    resolveFlagGroup(state, action) {
      const key = action.payload;
      if (key && !state.resolvedGroups.includes(key)) {
        state.resolvedGroups.push(key);
      }
    },
    /**
     * Store the full brand-safety preferences map emitted by BrandSafetySeverityPanel's onChange
     * (already normalized to { [Flag]: { severe, moderate, low, minor } } by brandSafetySeverity).
     * Marks the filters dirty so a concurrent report load won't clobber this edit before it's saved.
     */
    setBrandSafetyFilters(state, action) {
      state.filtersBase = mergeUnderCeiling(
        state.filtersBase,
        action.payload,
        state.campaignFlagCeiling
      );
      state.filters = clampPreferencesToCeiling(state.filtersBase, state.campaignFlagCeiling);
      state.filtersDirty = true;
    },
    /**
     * "Reset to default" — re-enable every severity for every flag, minus anything the campaign
     * disabled (a user edit, so dirty). Clamping here is what makes the ceiling inescapable: this
     * reducer is also what the drawer's footer Reset hits, bypassing the severity panel entirely.
     */
    resetBrandSafetyFilters(state) {
      state.filtersBase = getDefaultPreferences();
      state.filters = clampPreferencesToCeiling(state.filtersBase, state.campaignFlagCeiling);
      state.filtersDirty = true;
    },
    /**
     * Initialize the filters from the persisted flag-config on report load — but only when the user
     * hasn't already edited them this session, so a slow load / retry can't wipe an in-progress edit.
     */
    hydrateBrandSafetyFilters(state, action) {
      if (!state.filtersDirty) {
        state.filtersBase = normalizePreferences(action.payload);
        state.filters = clampPreferencesToCeiling(state.filtersBase, state.campaignFlagCeiling);
      }
    },
    /**
     * Set the campaign ceiling (normalized map, or null when there is no campaign context) and
     * re-derive the filters from the UNCLAMPED base. Deriving from the base (not from the current
     * `filters`) is what makes this reversible: clamping the already-clamped map would intersect
     * every ceiling ever applied, so a campaign that re-enables a flag — or simply switching to a
     * more permissive campaign — could never restore it.
     */
    setCampaignFlagCeiling(state, action) {
      state.campaignFlagCeiling = action.payload ?? null;
      state.filters = clampPreferencesToCeiling(state.filtersBase, state.campaignFlagCeiling);
    },
    /** Record a per-post review verdict (Review tab). Payload: { postId, status }. */
    setPostReviewStatus(state, action) {
      const { postId, status } = action.payload || {};
      if (postId) state.reviewedPosts[postId] = status;
    },
    /**
     * Record a per-flagged-item verdict (Review tab AI Findings). Payload: { postId, key, status }.
     * Keyed by post so two posts flagged on the same category keep independent verdicts.
     */
    setDetectionReviewStatus(state, action) {
      const { postId, key, status } = action.payload || {};
      if (!postId || !key) return;
      if (!state.reviewedDetections[postId]) state.reviewedDetections[postId] = {};
      state.reviewedDetections[postId][key] = status;
    },
    /** Clear between influencers (and on a new scan / manual refresh) — also drops the report cache key. */
    resetBrandSafetyReport(state) {
      state.report = null;
      state.reportError = null;
      state.reportLoadingMore = false;
      state.reportFetchedCrd = null;
      state.resolvedGroups = [];
      // Back to "everything the campaign allows" — the ceiling itself is campaign-scoped and is
      // re-synced on the next mount, so it deliberately survives this reset.
      state.filtersBase = getDefaultPreferences();
      state.filters = clampPreferencesToCeiling(state.filtersBase, state.campaignFlagCeiling);
      state.filtersDirty = false;
      state.reviewedPosts = {};
      state.reviewedDetections = {};
    },
    setCampaignReport(state, action) {
      // A refetch replaces the whole report, and only Reverb knows about a check the summary has no row
      // for — carry that map over so a reconnect or settle mid-run does not blank the hero.
      const live = state.campaign.report?.liveChecks;
      state.campaign.report =
        action.payload && live && !action.payload.liveChecks
          ? { ...action.payload, liveChecks: live }
          : action.payload;
    },
    setCampaignLoading(state, action) {
      state.campaign.loading = action.payload;
    },
    setCampaignError(state, action) {
      state.campaign.error = action.payload;
    },
    setCampaignFlagged(state, action) {
      state.campaign.flagged = action.payload;
    },
    setCampaignFlaggedLoading(state, action) {
      state.campaign.flaggedLoading = action.payload;
    },
    setCampaignFlaggedError(state, action) {
      state.campaign.flaggedError = action.payload;
    },
    setCampaignFlaggedLoadingMore(state, action) {
      state.campaign.flaggedLoadingMore = action.payload;
    },
    /**
     * Append a further page of flagged posts (Load more) to the loaded flaggedPosts feed, de-duping posts
     * and media rows by post id and replacing the cursor pagination. The campaign-wide `summary` is
     * identical across pages, so it is left untouched.
     */
    appendCampaignFlaggedPosts(state, action) {
      const { flagged } = state.campaign;
      if (!flagged) return;
      const { posts = [], media = [], pagination = null } = action.payload || {};
      const existingPosts = Array.isArray(flagged.posts) ? flagged.posts : [];
      const seenPosts = new Set(existingPosts.map((p) => String(p.id)));
      flagged.posts = existingPosts.concat(
        (Array.isArray(posts) ? posts : []).filter((p) => p && !seenPosts.has(String(p.id)))
      );
      const existingMedia = Array.isArray(flagged.media) ? flagged.media : [];
      const seenMedia = new Set(existingMedia.map((m) => String(m.postId)));
      flagged.media = existingMedia.concat(
        (Array.isArray(media) ? media : []).filter((m) => m && !seenMedia.has(String(m.postId)))
      );
      if (pagination) flagged.pagination = pagination;
    },
    resetCampaignBrandSafety(state) {
      state.campaign = {
        report: null,
        loading: false,
        error: null,
        flagged: null,
        flaggedLoading: false,
        flaggedError: null,
        flaggedLoadingMore: false,
      };
    },
    // Live `.checks.updated`: patch matched creators in place, record every crd's status, and re-derive
    // the hero status so a running→complete transition shows without a re-fetch. No-op before load.
    applyCampaignChecksUpdate(state, action) {
      const { report } = state.campaign;
      if (!report) return;
      const { creatorChecks = [] } = action.payload || {};
      if (!creatorChecks.length) return;

      const byCrd = {};
      creatorChecks.forEach((check) => {
        if (check.crd) byCrd[check.crd] = check;
      });
      (Array.isArray(report.creators) ? report.creators : []).forEach((creator) => {
        const update = byCrd[String(creator.crd)];
        if (!update) return;
        // Only overwrite with a real status — a status-less event must not erase a known verdict.
        if (update.status) creator.checkStatus = update.status;
        if (update.flags && typeof update.flags === 'object') creator.liveFlags = update.flags;
      });

      // Reverb is the only source that knows a check the summary has no row for yet — keep every crd.
      const liveChecks = { ...(report.liveChecks || {}) };
      Object.entries(byCrd).forEach(([crd, check]) => {
        if (check.status) liveChecks[crd] = check.status;
      });
      report.liveChecks = liveChecks;

      // Re-derive from the FULL per-creator union (summary + liveChecks), not this event alone: a partial
      // event carrying only the creators that just succeeded must not flip the hero to COMPLETE while
      // others are still running.
      report.status = deriveCampaignStatus(report);
    },
  },
});

export const {
  setReport,
  setReportLoading,
  setReportLoadingMore,
  setReportError,
  appendReportPosts,
  setReportFetchedCrd,
  cacheReport,
  resolveFlagGroup,
  setBrandSafetyFilters,
  resetBrandSafetyFilters,
  hydrateBrandSafetyFilters,
  setCampaignFlagCeiling,
  setPostReviewStatus,
  setDetectionReviewStatus,
  resetBrandSafetyReport,
  setCampaignReport,
  setCampaignLoading,
  setCampaignError,
  setCampaignFlagged,
  setCampaignFlaggedLoading,
  setCampaignFlaggedError,
  setCampaignFlaggedLoadingMore,
  appendCampaignFlaggedPosts,
  resetCampaignBrandSafety,
  applyCampaignChecksUpdate,
} = BrandSafetySlice.actions;

// ─── API endpoints (brand-safety-api.md) ──────────────
// Every endpoint is live. Each thunk calls the real endpoint and returns the raw response; the
// component/contract callers branch on `isApiSuccess(resp)`. Only `fetchBrandSafetyReport` writes to
// Redux, so only it handles success/error itself.

/**
 * 1. POST creator/brand-safety/availability — scope + credit estimate for the run modal. Keyed on
 * `username` + `socialNetwork`; accepts a single creator (array of 1) or a bulk selection. The
 * response carries an aggregate `credits` block.
 */
export const checkBrandSafetyAvailability = (params = {}) => () =>
  apiPost(EP.availability, buildAvailabilityBody(params), { timeout: AVAILABILITY_TIMEOUT_MS });

/** 2. POST creator/brand-safety/run — trigger a check across a batch of influencers. */
export const runBrandSafetyCheck = (params = {}) => () => apiPost(EP.run, buildRunBody(params));

/**
 * Load + aggregate the campaign report section (Influencers cards + hero). Wraps creatorReports (doc #2):
 * its `data.creatorReports[]` are aggregated into one campaign view-model (`aggregateCampaignBrandSafety`)
 * written to `state.BrandSafety.campaign`. `campaignId` is required. A failed call sets the campaign error
 * so the tab shows the error state instead of a blank screen.
 *
 * @param {object} args
 * @param {string|number} args.campaignId
 * @param {string} [args.campaignName]
 * @returns {(dispatch) => Promise<object|null>}
 */
export const fetchCampaignBrandSafetyReport = ({ campaignId, campaignName } = {}) => (dispatch) => {
  if (!campaignId) {
    dispatch(setCampaignError('Missing campaign id.'));
    return Promise.resolve(null);
  }
  dispatch(setCampaignLoading(true));
  dispatch(setCampaignError(null));

  return apiGet(EP.creatorReports(campaignId), { timeout: CAMPAIGN_REPORT_TIMEOUT_MS }).then(
    (resp) => {
      dispatch(setCampaignLoading(false));
      if (isApiSuccess(resp)) {
        // Pass the whole `data` object — the aggregator reads campaign-level status/updatedAt/totalCreators
        // alongside creatorReports[].
        const model = aggregateCampaignBrandSafety(resp?.data, campaignName);
        dispatch(setCampaignReport(model));
        return model;
      }
      dispatch(setCampaignReport(null));
      dispatch(
        setCampaignError(resp?.error ?? resp?.message ?? 'Failed to load campaign brand safety.')
      );
      return null;
    }
  );
};

/**
 * 4. PATCH creator/brand-safety/creator-report-details/{crd}/posts/{postTableId} — approve/reject one
 * post. Unique on (workspace, creator, post): re-deciding updates in place, and the verdict is shared
 * workspace-wide for the creator rather than scoped to one check.
 */
export const reviewBrandSafetyPost = ({
  creatorReportDetailsId,
  postTableId,
  action,
  notes,
} = {}) => () => apiPatch(EP.reviewPost(creatorReportDetailsId, postTableId), { action, notes });

/**
 * Load + aggregate the campaign review section (Media view + Review tab). Wraps the flaggedPosts endpoint
 * (doc #4): its campaign-wide `summary` + FLAT cursor feed are aggregated via
 * `aggregateCampaignFlaggedPosts` into `state.BrandSafety.campaign.flagged`. The report section (doc #2)
 * has no per-post data, so this is the source for the Media view + Review tab.
 *
 * Cursor-paginated: the initial load fetches the first page at the max `limit`; `append: true`
 * ("Load more") sends the previous response's `nextCursor` and merges the next page into the loaded feed,
 * never blocking the loaded view (or surfacing a blocking error) if the extra page fails.
 *
 * @param {object} args
 * @param {string|number} args.campaignId
 * @param {string} [args.cursor]           resume token from the previous page's `pagination.nextCursor`
 * @param {number} [args.limit]            posts per page (defaults to the endpoint cap)
 * @param {boolean} [args.append=false]    merge the page in rather than replacing the loaded feed
 * @returns {(dispatch) => Promise<object|null>}
 */
export const fetchCampaignFlaggedPosts = ({
  campaignId,
  cursor,
  limit = BS_FLAGGED_POSTS_LIMIT,
  append = false,
} = {}) => (dispatch) => {
  if (!campaignId) {
    if (!append) dispatch(setCampaignFlaggedError('Missing campaign id.'));
    return Promise.resolve(null);
  }
  const opts = { cursor, limit };

  // Load more: fetch the next page and append; the loaded feed stays intact on failure.
  if (append) {
    dispatch(setCampaignFlaggedLoadingMore(true));
    return apiGet(EP.flaggedPosts(campaignId, opts), { timeout: CAMPAIGN_REPORT_TIMEOUT_MS }).then(
      (resp) => {
        dispatch(setCampaignFlaggedLoadingMore(false));
        if (!isApiSuccess(resp)) return resp;
        const model = aggregateCampaignFlaggedPosts(resp?.data);
        dispatch(
          appendCampaignFlaggedPosts({
            posts: model.posts,
            media: model.media,
            pagination: model.pagination,
          })
        );
        return model;
      }
    );
  }

  dispatch(setCampaignFlaggedLoading(true));
  dispatch(setCampaignFlaggedError(null));

  return apiGet(EP.flaggedPosts(campaignId, opts), { timeout: CAMPAIGN_REPORT_TIMEOUT_MS }).then(
    (resp) => {
      dispatch(setCampaignFlaggedLoading(false));
      if (isApiSuccess(resp)) {
        const model = aggregateCampaignFlaggedPosts(resp?.data);
        dispatch(setCampaignFlagged(model));
        return model;
      }
      dispatch(setCampaignFlagged(null));
      dispatch(
        setCampaignFlaggedError(
          resp?.error ?? resp?.message ?? 'Failed to load brand safety flagged posts.'
        )
      );
      return null;
    }
  );
};

/** 6. GET creator/brand-safety/creator-report-details/{crd}/checks — checks for one creatorReportDetails. */
export const fetchCreatorBrandSafetyChecks = (creatorReportDetailsId) => () =>
  apiGet(EP.creatorChecks(creatorReportDetailsId));

/**
 * 7. POST creator/brand-safety/posts/reviews — bulk approve/reject 1–100
 * { creatorReportDetailsId, postTableId } pairs with one shared `action`. Best-effort: the endpoint
 * always returns 200 with `{ reviewed: [], failed: [{ ...item, reason }] }` rather than failing the batch.
 *
 * @param {object} args
 * @param {'approved'|'rejected'} args.action
 * @param {Array<{ creatorReportDetailsId:number, postTableId:number }>} args.items
 */
export const reviewBrandSafetyPosts = ({ action, items = [] } = {}) => () =>
  apiPost(EP.reviewPosts, { action, items });

/**
 * 10. POST .../creator-report-details/{crd}/detections/reviews — approve/reject INDIVIDUAL flagged
 * items across the posts of ONE creator report (AAPU-6775). Each item names its own `postTableId`, so
 * one request can span several posts; the BE caps `detections` at `BS_DETECTION_REVIEW_BATCH_SIZE`
 * (200). The full request/response contract lives with the body builder in `brandSafetyApi.js`.
 *
 * @param {object} args
 * @param {number|string} args.creatorReportDetailsId
 * @param {'approved'|'rejected'} args.action
 * @param {Array<{ postTableId:number|string, detection:object }>} args.items
 */
export const reviewBrandSafetyDetections = ({
  creatorReportDetailsId,
  action,
  items = [],
} = {}) => () =>
  apiPost(EP.reviewDetections(creatorReportDetailsId), buildDetectionReviewBody({ action, items }));

/**
 * 10b. POST /creator/brand-safety/detections/reviews — the same per-detection review, unscoped: every
 * detection in the body carries its own `creatorReportDetailsId`, so ONE request can span several
 * creators (AAPU-6775). This is what the CAMPAIGN-level Bulk Resolve modal sends, since a campaign
 * flag's occurrences are cross-creator by nature and the per-creator route would need one request per
 * creator. Same 200-detection cap, same response, plus a `creator_not_visible` failure reason for a
 * crd outside the workspace (the per-creator route 404s instead).
 *
 * @param {object} args
 * @param {'approved'|'rejected'} args.action
 * @param {Array<{ postTableId:number|string, creatorReportDetailsId:number|string, detection:object }>} args.items
 */
export const reviewBrandSafetyDetectionsAcrossCreators = ({ action, items = [] } = {}) => () =>
  apiPost(
    EP.reviewDetectionsAcrossCreators,
    buildDetectionReviewBody({ action, items, includeCreator: true })
  );

/**
 * 12. POST .../detections/approve-all — approve EVERY pending occurrence of ONE flag in a scope,
 * without enumerating them (AAPU-6775). The flag is built from the detection the same way
 * `fetchBrandSafetyFlagOccurrences` builds its query, so the sweep covers exactly what the modal
 * counted. A `campaignId` picks the campaign route (which aggregates across every creator, hence the
 * longer timeout), otherwise the creator one.
 *
 * Raw and store-free — the overlay/revert logic lives in `approveAllReviewDetections`. Throttled at
 * 30 requests/minute on the BE, so this is a one-click action, never a loop.
 *
 * @param {object} args
 * @param {string|number} [args.creatorReportDetailsId]  creator scope
 * @param {string|number} [args.campaignId]              campaign scope
 * @param {object} args.detection  the normalized detection whose card opened the modal
 * @returns {() => Promise<object>} the raw response
 */
export const approveAllBrandSafetyDetections = ({
  creatorReportDetailsId,
  campaignId,
  detection,
} = {}) => () => {
  const flag = {
    category: detection?.rawCategory ?? null,
    label: detection?.label ?? null,
    brand: detection?.brand ?? null,
  };
  const scoped = campaignId != null;
  const endpoint = scoped
    ? EP.campaignApproveAllDetections(campaignId)
    : EP.creatorApproveAllDetections(creatorReportDetailsId);
  return apiPost(
    endpoint,
    buildApproveAllBody(flag),
    scoped ? { timeout: CAMPAIGN_REPORT_TIMEOUT_MS } : {}
  );
};

/**
 * 11. GET .../creator-report-details/{crd}/detections — or, for the campaign scope,
 * GET /campaign/brand-safety/{campaignId}/detections — every occurrence of ONE flag across the whole
 * scope (AAPU-6775). Both routes are live in the BE; the request/response contract lives with the
 * endpoint builders in `brandSafetyApi.js`.
 *
 * Read-only and store-free: the Bulk Resolve modal owns the fetched list, so this resolves straight to
 * one page of normalized occurrences plus the scope's TRUE total. The BE returns at most
 * `BS_FLAG_OCCURRENCES_MAX_ITEMS` (15) items per page and always reports the real count in
 * `data.total`, so the modal reads `total` to pick its experience: at most 15 ⇒ every occurrence is
 * on this one page, rendered as selectable tiles resolved through the review route; more than 15 ⇒
 * the large-list experience, "Approve all N" (`approveAllReviewDetections`) or "Review individually"
 * paging through with this thunk. A `campaignId` picks the campaign route, otherwise the creator one.
 * Never throws and never rejects — a failure resolves to `{ items: [], total: 0, page }` and the
 * modal shows its empty (or page-failed) state.
 *
 * The `page`/`pageSize` query params are an assumption pending BE confirmation; they are named in
 * `buildFlagOccurrencesQuery` alone (see the ep11 contract block in `brandSafetyApi.js`).
 *
 * @param {object} args
 * @param {string|number} [args.creatorReportDetailsId]  creator scope (the single-influencer report)
 * @param {string|number} [args.campaignId]              campaign scope (every creator's posts)
 * @param {object} args.detection  the normalized detection whose card opened the modal
 * @param {number} [args.page=1]      1-based page of occurrences to fetch
 * @param {number} [args.pageSize=BS_FLAG_OCCURRENCES_MAX_ITEMS]  items per page
 * @returns {() => Promise<{ items: Array<{ post: object, detection: object, status: string }>, total: number, page: number }>}
 */
export const fetchBrandSafetyFlagOccurrences = ({
  creatorReportDetailsId,
  campaignId,
  detection,
  page = 1,
  pageSize = BS_FLAG_OCCURRENCES_MAX_ITEMS,
} = {}) => () => {
  // Every early exit still reports the page that was asked for, so the modal's pager state stays in
  // step with what it rendered instead of snapping back to page 1.
  // `failed` tells the modal a request actually went wrong (vs. a scope that genuinely has nothing),
  // so a broken page fetch shows a retry message instead of collapsing into an empty small list.
  const empty = { items: [], total: 0, page };
  const failed = { ...empty, failed: true };
  if (!detection) return Promise.resolve(empty);
  const flag = {
    category: detection.rawCategory ?? null,
    label: detection.label ?? null,
    brand: detection.brand ?? null,
  };
  const scoped = campaignId != null;
  if (!scoped && !creatorReportDetailsId) return Promise.resolve(empty);
  const paging = { page, pageSize };
  const endpoint = scoped
    ? EP.campaignFlagOccurrences(campaignId, flag, paging)
    : EP.creatorFlagOccurrences(creatorReportDetailsId, flag, paging);
  return apiGet(endpoint, scoped ? { timeout: CAMPAIGN_REPORT_TIMEOUT_MS } : {}).then((resp) => {
    if (!isApiSuccess(resp)) return failed;
    // The payload is BE-controlled and brand new; the normalizer is defensive, but a surprise type
    // (a field that's wrong rather than merely absent) must not take the modal down with it.
    try {
      const items = normalizeFlagOccurrences(resp?.data ?? null);
      const rawTotal = Number(resp?.data?.total);
      const total = Number.isFinite(rawTotal) ? rawTotal : items.length;
      // Trust the BE's echoed page when it sends one; the requested page is the fallback, since the
      // paging params themselves are still an assumption.
      const rawPage = Number(resp?.data?.page);
      // How many of `total` are still pending — what "Approve all" would actually touch. Older
      // payloads don't send it; `null` lets the modal fall back to `total`.
      const rawPending = Number(resp?.data?.pendingApprovalCount);
      const pendingApprovalCount = Number.isFinite(rawPending) ? Math.max(0, rawPending) : null;
      return {
        items,
        total,
        page: Number.isFinite(rawPage) ? rawPage : page,
        pendingApprovalCount,
      };
    } catch (e) {
      return failed;
    }
  });
};

/**
 * 8. POST creator/brand-safety/flag-config — persist the Filters drawer's per-flag severity map for a
 * creatorReportDetails. The saved config is echoed back on the next checks response
 * (`data.flagConfig`), which `fetchBrandSafetyReport` hydrates the drawer from. Fire-and-forget: the
 * drawer already applied the change locally, so the caller only needs the raw response to detect a
 * failed persist.
 *
 * @param {object} args
 * @param {string|number} args.creatorReportDetailsId
 * @param {object} args.data  { [Flag]: { severe, moderate, low, minor } }
 */
export const saveBrandSafetyFlagConfig = ({ creatorReportDetailsId, data } = {}) => () =>
  apiPost(EP.flagConfig, { creatorReportDetailsId, data });

/**
 * Load the single-influencer Safety Analysis Report. Backed by endpoint 9
 * (`GET /creator/brand-safety/report/{crd}`), whose response is normalized into the report
 * view-model. `crd` is required — without it there is no report to load. A failed call, an
 * unexpected/partial payload, or a normalization error all set the report error so the page shows
 * the error notice instead of crashing or showing a blank report.
 *
 * @param {object} params
 * @param {string} params.platform
 * @param {string} params.username
 * @param {string} [params.name]                 display name from the calling row, if known
 * @param {string} [params.avatarUrl]
 * @param {string|number} [params.creatorReportDetailsId]  `crd` — the endpoint-9 key
 * @returns {(dispatch) => Promise<object|null>}
 */
export const fetchBrandSafetyReport = ({
  platform,
  username,
  name,
  avatarUrl,
  creatorReportDetailsId,
  // Pagination for the flagged-posts list. Omit both to fetch every flagged post at once (no pagination).
  page,
  pageSize,
  // `append` = a "Load more" page: merge the new posts into the loaded report rather than replacing it,
  // and never disturb the loaded report (or surface a blocking error) if the extra page fails.
  append = false,
  // `force` bypasses the cache (an explicit Refresh, or a re-fetch after a new scan).
  force = false,
} = {}) => (dispatch, getState) => {
  if (!platform || !username) {
    if (!append) dispatch(setReportError('Missing influencer identity.'));
    return Promise.resolve(null);
  }
  if (!creatorReportDetailsId) {
    if (!append) dispatch(setReportError('Missing creator report details id.'));
    return Promise.resolve(null);
  }

  const identity = { platform, username, name, avatarUrl };

  // ── Load more: fetch the next page and append; the loaded report stays intact on failure. ──
  if (append) {
    dispatch(setReportLoadingMore(true));
    return apiGet(EP.report(creatorReportDetailsId, { page, pageSize })).then((resp) => {
      dispatch(setReportLoadingMore(false));
      if (!isApiSuccess(resp)) return resp;
      try {
        const normalized = normalizeBrandSafetyReport(resp?.data ?? null, identity);
        if (normalized) {
          dispatch(
            appendReportPosts({
              posts: normalized.posts,
              brands: normalized.brands,
              pagination: normalized.pagination,
            })
          );
        }
        return normalized;
      } catch (e) {
        return null;
      }
    });
  }

  const key = String(creatorReportDetailsId);
  const { report, reportFetchedCrd, reportsByCrd } = getState().BrandSafety || {};
  // Active-slot cache hit: this crd's report already sits in the store — serve it without a network call.
  // It carries whatever pages were already appended, so navigating away and back preserves the loaded posts.
  if (!force && report && String(reportFetchedCrd) === key) {
    return Promise.resolve(report);
  }
  // Cross-creator cache hit: this creator was loaded earlier and swapped out — restore their report (all
  // pages appended so far) + saved flag config from the per-creator cache, with no network call.
  const cached = !force ? reportsByCrd?.[key] : null;
  if (cached && cached.report) {
    // Clear the previous creator's transient view state (report slot, reviewedPosts, filters) first.
    if (reportFetchedCrd != null && String(reportFetchedCrd) !== key) {
      dispatch(resetBrandSafetyReport());
    }
    dispatch(setReport(cached.report));
    dispatch(setReportFetchedCrd(key));
    dispatch(
      hydrateBrandSafetyFilters(
        cached.flagConfig ? normalizePreferences(cached.flagConfig) : getDefaultPreferences()
      )
    );
    return Promise.resolve(cached.report);
  }
  // Switching influencers (or a forced refresh): drop the prior report + view state first so nothing
  // stale flashes under the new header (the loader covers the gap until the fresh report lands).
  if (force || (reportFetchedCrd != null && String(reportFetchedCrd) !== key)) {
    dispatch(resetBrandSafetyReport());
  }

  dispatch(setReportLoading(true));
  dispatch(setReportError(null));

  return apiGet(EP.report(creatorReportDetailsId, { page, pageSize })).then((resp) => {
    dispatch(setReportLoading(false));
    if (isApiSuccess(resp)) {
      // Normalization is defensive by design (every field is optional-chained/defaulted), but the
      // payload shape is still new and BE-controlled — a try/catch keeps any surprise (a field that's
      // the wrong type instead of merely absent) from taking down the whole page.
      try {
        const data = resp?.data ?? null;
        const normalized = normalizeBrandSafetyReport(data, identity);
        if (!normalized) {
          dispatch(setReport(null));
          dispatch(setReportError('The safety report is empty.'));
          return null;
        }
        dispatch(setReport(normalized));
        // Mark the cache key so a revisit with this crd reads the store instead of re-fetching.
        dispatch(setReportFetchedCrd(String(creatorReportDetailsId)));
        // Snapshot into the per-creator cache so switching influencers and back restores this without a
        // fetch. Subsequent "Load more" appends mirror into this same entry (see appendReportPosts).
        dispatch(
          cacheReport({
            crd: String(creatorReportDetailsId),
            report: normalized,
            flagConfig: data?.flagConfig?.data ?? null,
          })
        );
        // Hydrate the Filters drawer from the persisted flag config, when the report carries one
        // (`data.flagConfig.data`). Normalize first: the reducer stores its payload raw, and the BE map
        // may be partial — `normalizePreferences` fills gaps / drops unknowns. Absent config ⇒ defaults,
        // so a fresh report never inherits a prior creator's in-memory filters. `hydrateBrandSafetyFilters`
        // yields to an unsaved in-progress edit, so a slow load / retry can't clobber the user's changes.
        const savedFilters = data?.flagConfig?.data;
        dispatch(
          hydrateBrandSafetyFilters(
            savedFilters ? normalizePreferences(savedFilters) : getDefaultPreferences()
          )
        );
        return normalized;
      } catch (e) {
        dispatch(setReport(null));
        dispatch(setReportError('Failed to load the safety report.'));
        return null;
      }
    }
    dispatch(setReport(null));
    // The BE distinguishes 404 (report not visible to the workspace — never 403) from 422 (page < 1 or
    // pageSize outside 1–100). `doApiCallGet` surfaces the HTTP status on the error payload.
    // eslint-disable-next-line no-underscore-dangle
    const httpStatus = resp?._httpStatus ?? resp?.httpStatus;
    let errorMessage;
    if (httpStatus === 404) errorMessage = "This report isn't available for your workspace.";
    else if (httpStatus === 422) errorMessage = 'Invalid page request.';
    else errorMessage = resp?.error ?? resp?.message ?? 'Failed to load the safety report.';
    dispatch(setReportError(errorMessage));
    return null;
  });
};

/**
 * Resolve one post from the Review tab: optimistically overlay the verdict, then hit endpoint 4.
 * @param {object} args
 * @param {number|string} [args.creatorReportDetailsId]
 * @param {string} args.postId
 * @param {'approved'|'rejected'} [args.action]
 */
export const resolveReviewPost = ({ creatorReportDetailsId, postId, action = 'approved' } = {}) => (
  dispatch
) => {
  if (!postId) return Promise.resolve(null);
  dispatch(setPostReviewStatus({ postId, status: action }));
  return dispatch(reviewBrandSafetyPost({ creatorReportDetailsId, postTableId: postId, action }));
};

/**
 * Resolve a batch of posts (Flag Type "Resolve all" / AI-findings "Bulk Resolve") via endpoint 7:
 * optimistically overlay the verdict on every item, POST the batch, then revert any the server
 * reports in `failed[]` back to `pending` so the overlay reflects reality. The mock never fails, so
 * the revert only bites against the live endpoint.
 *
 * @param {object} args
 * @param {Array<{ creatorReportDetailsId:number, postTableId:number }>} args.items
 * @param {'approved'|'rejected'} [args.action]
 */
export const resolveReviewPosts = ({ items = [], action = 'approved' } = {}) => (dispatch) => {
  if (!items.length) return Promise.resolve(null);
  items.forEach((item) =>
    dispatch(setPostReviewStatus({ postId: String(item.postTableId), status: action }))
  );
  return dispatch(reviewBrandSafetyPosts({ action, items })).then((resp) => {
    (resp?.data?.failed ?? []).forEach((failed) =>
      dispatch(setPostReviewStatus({ postId: String(failed.postTableId), status: 'pending' }))
    );
    return resp;
  });
};

/**
 * Resolve INDIVIDUAL flagged items across one or more posts, in as few requests as the routes allow.
 * Both routes cap `detections` at `BS_DETECTION_REVIEW_BATCH_SIZE` (200), so the work is always
 * chunked; `crossCreator` picks which one:
 *
 *   false (default)  endpoint 10, scoped to ONE creatorReportDetailsId — the groups are bucketed per
 *                    creator and each bucket chunked at 200. This is the single-influencer report,
 *                    where everything belongs to one creator anyway.
 *   true             endpoint 10b (`POST /creator/brand-safety/detections/reviews`), where each
 *                    detection carries its own crd — ONE stream chunked at 200, no per-creator
 *                    bucketing. The campaign modal uses this: 200 occurrences spread over 9 creators
 *                    is 1 request here and would be 9 on the per-creator route.
 *
 * The chunking is what lets the large-list modal's "Review individually" send an arbitrary selection
 * gathered across pages; only "Approve all" bypasses this for `approveAllReviewDetections`.
 *
 * Optimistically overlays the verdict on every detection key, POSTs the batches, then reverts the keys
 * the server reports in `data.failed` back to `pending`. The per-post-level verdict moves on two
 * rules, in this order:
 *
 *   the FE's local rule — a post flips only once EVERY flagged item on it is resolved (that is the
 *   whole point of per-item review), so `setPostReviewStatus` fires for a group only when its full
 *   key set (`postDetectionKeys`, from the container that owns the post) comes out non-pending, and
 *   is rolled back if the server rejects any of that post's items. Items resolved in a prior session
 *   count too: their persisted verdicts arrive in `persistedReviewActions` and seed the merge, so
 *   clearing a post's last still-pending item flips it even though the earlier verdicts never
 *   entered this session's overlay.
 *
 *   then the server's — `data.autoApprovedPostTableIds` names the posts the BE itself flipped, and it
 *   is AUTHORITATIVE: it is applied last, over any local revert, because the BE sees every flagged
 *   item on the post while the local rule only sees the ones this client happened to load (the
 *   cross-creator modal, in particular, holds one 15-item page of other creators' posts).
 *
 * `failed[]` only echoes each item's `key`, so a key → postIds map is built before sending. Keys are
 * unique per post in practice (occurrence keys are post-prefixed); if one key does appear on several
 * posts it is reverted on all of them, which is the conservative reading.
 *
 * @param {object} args
 * @param {Array<{
 *   creatorReportDetailsId: number|string,
 *   postId: string|number,
 *   detections: Array<object>,
 *   postDetectionKeys?: string[],
 *   persistedReviewActions?: Object<string,string>,
 * }>} args.groups  one entry per post
 * @param {'approved'|'rejected'} [args.action]
 * @param {boolean} [args.crossCreator=false]  send one cross-creator stream instead of per-creator
 *        buckets (the campaign-level modal)
 * @returns {Promise<Array<object>>} one raw response per request sent
 */
export const resolveReviewDetectionsBatch = ({
  groups = [],
  action = 'approved',
  crossCreator = false,
} = {}) => (dispatch, getState) => {
  // Drop groups with nothing to send, normalise ids, and keep only detections that carry a key.
  const prepared = (Array.isArray(groups) ? groups : [])
    .map((group) => ({
      creatorReportDetailsId: group?.creatorReportDetailsId ?? null,
      postId: group?.postId != null && group.postId !== '' ? String(group.postId) : null,
      detections: (Array.isArray(group?.detections) ? group.detections : []).filter((d) => d?.key),
      postDetectionKeys: Array.isArray(group?.postDetectionKeys) ? group.postDetectionKeys : [],
      persistedReviewActions: group?.persistedReviewActions ?? {},
    }))
    .filter(
      (group) => group.postId && group.creatorReportDetailsId != null && group.detections.length
    );
  if (!prepared.length) return Promise.resolve([]);

  // Snapshot the overlay BEFORE this batch and merge locally, so the "is the post fully resolved now?"
  // test doesn't depend on the dispatches below having already landed in the store. The persisted
  // verdicts seed the merge first (lowest precedence), then this session's overlay, then this batch —
  // mirroring the effective-status precedence the Review view renders with.
  const overlay = getState().BrandSafety?.reviewedDetections ?? {};
  const postResolved = {};
  const postsByKey = new Map();
  prepared.forEach(({ postId, detections, postDetectionKeys, persistedReviewActions }) => {
    const merged = { ...persistedReviewActions, ...(overlay[postId] ?? {}) };
    detections.forEach(({ key }) => {
      dispatch(setDetectionReviewStatus({ postId, key, status: action }));
      merged[key] = action;
      if (!postsByKey.has(key)) postsByKey.set(key, new Set());
      postsByKey.get(key).add(postId);
    });
    postResolved[postId] =
      postDetectionKeys.length > 0 &&
      postDetectionKeys.every((key) => (merged[key] ?? 'pending') !== 'pending');
    if (postResolved[postId]) dispatch(setPostReviewStatus({ postId, status: action }));
  });

  const requests = [];
  if (crossCreator) {
    // One stream, each item naming its own creator — chunked at the route's cap and nothing else.
    const items = [];
    prepared.forEach(({ creatorReportDetailsId, postId, detections }) => {
      detections.forEach((detection) =>
        items.push({ postTableId: postId, creatorReportDetailsId, detection })
      );
    });
    for (let i = 0; i < items.length; i += BS_DETECTION_REVIEW_BATCH_SIZE) {
      requests.push(
        dispatch(
          reviewBrandSafetyDetectionsAcrossCreators({
            action,
            items: items.slice(i, i + BS_DETECTION_REVIEW_BATCH_SIZE),
          })
        )
      );
    }
  } else {
    // One bucket per creator report, each chunked at the route's cap.
    const buckets = new Map();
    prepared.forEach(({ creatorReportDetailsId, postId, detections }) => {
      const id = String(creatorReportDetailsId);
      if (!buckets.has(id)) buckets.set(id, []);
      detections.forEach((detection) => buckets.get(id).push({ postTableId: postId, detection }));
    });
    buckets.forEach((items, creatorReportDetailsId) => {
      for (let i = 0; i < items.length; i += BS_DETECTION_REVIEW_BATCH_SIZE) {
        requests.push(
          dispatch(
            reviewBrandSafetyDetections({
              creatorReportDetailsId,
              action,
              items: items.slice(i, i + BS_DETECTION_REVIEW_BATCH_SIZE),
            })
          )
        );
      }
    });
  }

  return Promise.all(requests).then((responses) => {
    const revertedPosts = new Set();
    responses.forEach((resp) => {
      (resp?.data?.failed ?? []).forEach((item) => {
        (postsByKey.get(item?.key) ?? []).forEach((postId) => {
          dispatch(setDetectionReviewStatus({ postId, key: item.key, status: 'pending' }));
          revertedPosts.add(postId);
        });
      });
    });
    // A partial failure means the post is no longer fully resolved — put its badge back.
    revertedPosts.forEach((postId) => {
      if (postResolved[postId]) dispatch(setPostReviewStatus({ postId, status: 'pending' }));
    });
    // The BE's own verdict, applied LAST so it wins over the revert above: these posts have no
    // outstanding flagged items left server-side, whatever this client can see of them.
    responses.forEach((resp) => {
      const autoApproved = resp?.data?.autoApprovedPostTableIds;
      (Array.isArray(autoApproved) ? autoApproved : []).forEach((postTableId) => {
        if (postTableId == null) return;
        dispatch(setPostReviewStatus({ postId: String(postTableId), status: 'approved' }));
      });
    });
    return responses;
  });
};

/**
 * Resolve INDIVIDUAL flagged items on ONE post (AI Findings "Approve") — a single-group call into
 * `resolveReviewDetectionsBatch`; see it for the overlay / post-level-flip / revert semantics.
 *
 * @param {object} args
 * @param {number|string} [args.creatorReportDetailsId]
 * @param {string|number} args.postId
 * @param {Array<object>} args.detections            normalized detections to resolve
 * @param {string[]} [args.postDetectionKeys]        every flagged-item key on the post; omit to never
 *                                                   flip the post-level status
 * @param {Object<string,string>} [args.persistedReviewActions]  verdicts already stored on the post's
 *                                                   items (`{ [key]: 'approved'|'rejected' }`)
 * @param {'approved'|'rejected'} [args.action]
 * @returns {Promise<object|null>} the single response, or null when there was nothing to send
 */
export const resolveReviewDetections = ({
  creatorReportDetailsId,
  postId,
  detections = [],
  postDetectionKeys = null,
  persistedReviewActions = null,
  action = 'approved',
} = {}) => (dispatch) =>
  dispatch(
    resolveReviewDetectionsBatch({
      groups: [
        { creatorReportDetailsId, postId, detections, postDetectionKeys, persistedReviewActions },
      ],
      action,
    })
  ).then((responses) => responses[0] ?? null);

/**
 * Approve EVERY occurrence of one flag in a scope (endpoint 12) — the Bulk Resolve modal's default
 * write once a flag has more occurrences than one page holds (`BS_FLAG_OCCURRENCES_MAX_ITEMS`).
 * The alternative there is "Review individually", which pages through the occurrences and sends the
 * ticked ones through `resolveReviewDetectionsBatch` instead.
 *
 * The sweep happens server-side across the whole scope, so the optimistic overlay works the other
 * way round from `resolveReviewDetectionsBatch`: instead of marking the items we sent, it marks every
 * occurrence of the flag among the posts the caller HAS loaded (`posts`), matched by
 * `flagIdentityKey` — same `(category, label, brand)` identity the endpoint filters on, so a hit in
 * an image and one in a caption at 0:42 both go green. Anything not loaded simply arrives resolved on
 * the next fetch.
 *
 * Failure reverts exactly the keys this call touched. Success applies `autoApprovedPostTableIds`,
 * which is the only way the post-level badges can move here — the local "every item resolved" rule
 * can't be evaluated for posts the client never loaded.
 *
 * @param {object} args
 * @param {string|number} [args.creatorReportDetailsId]  creator scope (the single-influencer report)
 * @param {string|number} [args.campaignId]              campaign scope (every creator's posts)
 * @param {object} args.detection            the normalized detection whose card opened the modal
 * @param {Array<object>} [args.posts]       the loaded normalized posts to overlay optimistically
 * @returns {(dispatch) => Promise<object|null>} the response `data` ({ matched, approved, … }), or
 *          null when there was nothing to send or the call failed
 */
export const approveAllReviewDetections = ({
  creatorReportDetailsId,
  campaignId,
  detection,
  posts = [],
} = {}) => (dispatch) => {
  if (!detection) return Promise.resolve(null);
  if (campaignId == null && !creatorReportDetailsId) return Promise.resolve(null);
  // The endpoint 422s on an all-blank filter: approving every flag in a scope at once is deliberately
  // not expressible, so a detection that identifies nothing is dropped here rather than sent.
  const hasFilter = [detection.rawCategory, detection.label, detection.brand].some(
    (value) => value != null && String(value).trim() !== ''
  );
  if (!hasFilter) return Promise.resolve(null);

  const flagKey = flagIdentityKey(detection);
  const touched = [];
  (Array.isArray(posts) ? posts : []).forEach((post) => {
    const postId = post?.id != null ? String(post.id) : null;
    if (!postId) return;
    postDetections(post).forEach((loaded) => {
      if (!loaded?.key || flagIdentityKey(loaded) !== flagKey) return;
      touched.push({ postId, key: loaded.key });
      dispatch(setDetectionReviewStatus({ postId, key: loaded.key, status: 'approved' }));
    });
  });

  return dispatch(
    approveAllBrandSafetyDetections({ creatorReportDetailsId, campaignId, detection })
  ).then((resp) => {
    if (!isApiSuccess(resp)) {
      touched.forEach(({ postId, key }) =>
        dispatch(setDetectionReviewStatus({ postId, key, status: 'pending' }))
      );
      return null;
    }
    const autoApproved = resp?.data?.autoApprovedPostTableIds;
    (Array.isArray(autoApproved) ? autoApproved : []).forEach((postTableId) => {
      if (postTableId == null) return;
      dispatch(setPostReviewStatus({ postId: String(postTableId), status: 'approved' }));
    });
    return resp?.data ?? null;
  });
};

export { initialState as brandSafetyInitialState };

export default BrandSafetySlice.reducer;
